import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Plus, ShoppingCart } from "lucide-react";
import type { PurchaseOrderListItemDto } from "@procureflow/shared-types";
import {
  TransactionListShell,
  type ColumnDef,
  type StatusTab,
} from "@/components/transaction-list-shell";
import { TransactionStatusBadge } from "@/components/transaction-status-badge";
import { PO_STATUS_CONFIG } from "@/lib/status-config";
import { formatAmount, formatDate } from "@/lib/format";
import { ExportCsvButton } from "@/components/export-csv-button";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";

export const Route = createFileRoute("/purchase-orders")({
  head: () => ({
    meta: [
      { title: "Purchase Orders — ProcureFlow" },
      {
        name: "description",
        content: "View and track purchase orders.",
      },
    ],
  }),
  component: function POPageOrOutlet() {
    const child = useChildMatches();
    return child.length > 0 ? <Outlet /> : <POListPage />;
  },
});

const STATUS_TABS: StatusTab[] = [
  { label: "All", value: null },
  { label: "Draft", value: "Draft" },
  { label: "Sent", value: "Sent" },
  { label: "Partially Received", value: "PartiallyReceived" },
  { label: "Received", value: "Received" },
  { label: "Cancelled", value: "Cancelled" },
];

const COLUMNS: ColumnDef<PurchaseOrderListItemDto>[] = [
  {
    id: "po_number",
    header: "PO Number",
    cell: (po) => (
      <Link to="/purchase-orders/$id" params={{ id: po.id }} className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-primary/10 grid place-items-center text-primary shrink-0">
          <ShoppingCart className="h-3.5 w-3.5" />
        </div>
        <span className="font-mono text-xs font-medium hover:text-primary transition-colors">
          {po.po_number}
        </span>
      </Link>
    ),
  },
  {
    id: "vendor",
    header: "Vendor",
    cell: (po) => (
      <span className="font-medium truncate max-w-[220px] block">{po.vendor_name}</span>
    ),
  },
  {
    id: "amount",
    header: "Amount",
    headerClassName: "text-right",
    cellClassName: "text-right font-semibold tabular-nums",
    cell: (po) => formatAmount(po.total_amount),
  },
  {
    id: "status",
    header: "Status",
    cell: (po) => <TransactionStatusBadge config={PO_STATUS_CONFIG[po.status]} />,
  },
  {
    id: "created",
    header: "Created",
    cellClassName: "text-muted-foreground",
    cell: (po) => formatDate(po.created_at),
  },
  {
    // US-064 — distinct source RFQ number(s) for a PO aggregated from RFQs.
    // Kept last so the Status/Created column indices the e2e suite asserts
    // (status = 4th, created = 5th) are unchanged.
    id: "source_rfq",
    header: "Source RFQ",
    cellClassName: "font-mono text-xs text-muted-foreground",
    cell: (po) =>
      po.source_rfq_numbers && po.source_rfq_numbers.length > 0
        ? po.source_rfq_numbers.join(", ")
        : "—",
  },
];

function POListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const search = useSearch({ strict: false }) as Record<string, string | undefined>;
  const exportSearchParams = search.status
    ? new URLSearchParams({ status: search.status }).toString()
    : undefined;

  // US-064 — Create a PO from one or more RFQs. The builder POSTs
  // /api/purchase-orders which is ProcurementOfficer/Admin-only; gate to match.
  const canCreatePo = user?.role === "ProcurementOfficer" || user?.role === "Admin";

  return (
    <TransactionListShell<PurchaseOrderListItemDto>
      title="Purchase Orders"
      countNoun={{ singular: "order", plural: "orders" }}
      endpoint="/api/purchase-orders"
      tabs={STATUS_TABS}
      columns={COLUMNS}
      rowKey={(po) => po.id}
      rowClassName="cursor-pointer hover:bg-muted/30 transition-colors"
      onRowClick={(po) => navigate({ to: "/purchase-orders/$id", params: { id: po.id } })}
      emptyIcon={ShoppingCart}
      emptyMessage="No purchase orders found."
      headerAction={
        <div className="flex items-center gap-2">
          {canCreatePo && (
            <Button asChild data-testid="create-po-button">
              <Link to="/purchase-orders/build">
                <Plus className="h-3.5 w-3.5" /> Create PO
              </Link>
            </Button>
          )}
          <ExportCsvButton
            endpoint="/api/purchase-orders/export/csv"
            searchParams={exportSearchParams}
            fallbackFilename="purchase-orders.csv"
          />
        </div>
      }
    />
  );
}
